/**
 * Wizard-Schritt 1: nur noch Kanalauswahl (Vorlage lebt jetzt in Schritt 2,
 * Spec 2026-07-24 §4). Regel-Hinweis-Chip: `useAllTextRules()`.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Checkbox } from '@/components/v2/ui'
import { useChannels } from '@/hooks/useChannels'
import { useAllTextRules } from '@/hooks/useTextRules'
import { EID } from '@/lib/element-ids.registry'

import { ResearchPanel } from './ResearchPanel'

interface StepChannelsProps {
  productIds: string[]
  selectedChannelIds: string[]
  onToggleChannel: (channelId: string) => void
  researchEnabled: boolean
  onResearchChange: (enabled: boolean) => void
}

export function StepChannels({
  productIds,
  selectedChannelIds,
  onToggleChannel,
  researchEnabled,
  onResearchChange,
}: StepChannelsProps) {
  const { t } = useTranslation()
  const { data: channels = [], isLoading } = useChannels()
  const { data: rules = [] } = useAllTextRules()
  const channelsWithRules = useMemo(() => new Set(rules.map((rule) => rule.channel_id)), [rules])

  if (isLoading) {
    return <p className="text-sm text-neutral-500 dark:text-neutral-400">{t('common.loading')}</p>
  }

  return (
    <div className="space-y-4">
      <ResearchPanel productCount={productIds.length} enabled={researchEnabled} onChange={onResearchChange} />
      <section
        id={EID['text-bulk-wizard-channel-list-region']}
        aria-label={t('channelContent.wizard.stepChannels')}
        className="space-y-2"
      >
        {channels.map((channel) => (
          <div key={channel.id} className="flex items-center gap-3 rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
            <Checkbox
              id={`text-bulk-wizard-channel-${channel.id}-toggle`}
              checked={selectedChannelIds.includes(channel.id)}
              onChange={() => onToggleChannel(channel.id)}
              label={channel.custom_name || channel.channel_name}
            />
            {selectedChannelIds.includes(channel.id) && !channelsWithRules.has(channel.id) && (
              <span className="ml-auto rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-xs text-amber-700 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
                {t('channelContent.wizard.noRulesChip')}
              </span>
            )}
          </div>
        ))}
      </section>
    </div>
  )
}
